import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Loader2, MessageCircle, Trash2, Upload } from "lucide-react";
import { API, api, formatApiError } from "../api";
import { SETTINGS } from "../constants/testIds";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
import { FormField } from "../components/common/FormField";
import { SubmitButton } from "../components/common/SubmitButton";
import { useSuccessPulse } from "../hooks/useSuccessPulse";

const EMPTY_CONTACT = { legal_name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "" };
const EMPTY_GST = { gst_applicable: false, gst_rate_percent: "18", gstin: "", place_of_supply: "" };
const STATUS_LABELS = {
  active: "Connected", pending: "Awaiting test", failed: "Test failed", inactive: "Not connected",
};
const STATUS_COLORS = {
  active: "var(--status-active-text)", pending: "var(--status-overdue-text)",
  failed: "var(--scarlet-neg)", inactive: "var(--scarlet-text-secondary)",
};

// Org-wide branding used on every generated invoice (`routers/org_profile.py` + `invoice_utils.py`
// / `templates/invoice.html`): a logo that persists until explicitly replaced/deleted, contact +
// GST details, and an editable Terms & Conditions block that's pre-filled with a sensible starter
// (`DEFAULT_INVOICE_TERMS`) until a gym saves their own.
export default function Settings() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [gst, setGst] = useState(EMPTY_GST);
  const [terms, setTerms] = useState("");
  const [saveSuccess, savePulse] = useSuccessPulse();
  const [gstSuccess, gstPulse] = useSuccessPulse();
  const [termsSuccess, termsPulse] = useSuccessPulse();

  const profileQ = useQuery({ queryKey: ["org-profile"], queryFn: async () => (await api.get("/org-profile")).data });
  const integrationsQ = useQuery({ queryKey: ["integration-configs"], queryFn: async () => (await api.get("/integration-configs")).data.configs });

  useEffect(() => {
    if (!profileQ.data) return;
    const p = profileQ.data;
    setContact({
      legal_name: p.legal_name || "", phone: p.phone || "", email: p.email || "",
      line1: p.address?.line1 || "", line2: p.address?.line2 || "", city: p.address?.city || "",
      state: p.address?.state || "", pincode: p.address?.pincode || "",
    });
    setGst({
      gst_applicable: !!p.gst_applicable, gst_rate_percent: String(p.gst_rate_percent ?? 18),
      gstin: p.gstin || "", place_of_supply: p.place_of_supply || "",
    });
    setTerms(p.invoice_terms || "");
  }, [profileQ.data]);

  const uploadLogoMutation = useMutation({
    mutationFn: (file) => {
      const body = new FormData();
      body.append("file", file);
      return api.post("/org-profile/logo", body);
    },
    onSuccess: () => { toast.success("Logo updated."); queryClient.invalidateQueries({ queryKey: ["org-profile"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => api.delete("/org-profile/logo"),
    onSuccess: () => { toast.success("Logo removed."); queryClient.invalidateQueries({ queryKey: ["org-profile"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const saveContactMutation = useMutation({
    mutationFn: () => api.patch("/org-profile", {
      legal_name: contact.legal_name, phone: contact.phone, email: contact.email,
      address: { line1: contact.line1, line2: contact.line2, city: contact.city, state: contact.state, pincode: contact.pincode },
    }),
    onSuccess: () => savePulse(() => { toast.success("Business details saved."); queryClient.invalidateQueries({ queryKey: ["org-profile"] }); }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const saveGstMutation = useMutation({
    mutationFn: () => api.patch("/org-profile", {
      gst_applicable: gst.gst_applicable, gst_rate_percent: Number(gst.gst_rate_percent) || 0,
      gstin: gst.gstin, place_of_supply: gst.place_of_supply,
    }),
    onSuccess: () => gstPulse(() => { toast.success("GST settings saved."); queryClient.invalidateQueries({ queryKey: ["org-profile"] }); }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const saveTermsMutation = useMutation({
    mutationFn: () => api.patch("/org-profile", { invoice_terms: terms }),
    onSuccess: () => termsPulse(() => { toast.success("Terms & conditions saved."); queryClient.invalidateQueries({ queryKey: ["org-profile"] }); }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB."); return; }
    uploadLogoMutation.mutate(file);
  };

  const logoKey = profileQ.data?.logo_key;
  const logoUrl = logoKey ? `${API}/org-profile/logo?logo_key=${encodeURIComponent(logoKey)}` : null;

  return (
    <div data-testid={SETTINGS.page} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)" }}>Settings</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Your gym's branding and invoice defaults.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="solid-surface rounded-[14px] p-6 space-y-4">
        <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>Invoice logo</p>
        <div className="flex items-center gap-4">
          <div
            data-testid={SETTINGS.logoPreview}
            className="rounded-[10px] overflow-hidden flex items-center justify-center shrink-0"
            style={{ width: 96, height: 64, background: "var(--scarlet-surface-2)", border: "1px solid var(--scarlet-hairline)" }}
          >
            {logoUrl ? <img src={logoUrl} alt="Gym logo" className="w-full h-full object-contain" /> : <Building2 size={22} style={{ color: "var(--scarlet-text-secondary)" }} />}
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <span className="text-sm font-medium inline-flex items-center gap-1.5 rounded-full px-3.5 py-2" style={{ border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text)" }}>
                {uploadLogoMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {logoUrl ? "Replace logo" : "Upload logo"}
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={SETTINGS.logoInput} onChange={handleLogoChange} />
            </label>
            {logoUrl && (
              <Button
                type="button" variant="outline" size="sm" data-testid={SETTINGS.logoDeleteButton}
                disabled={deleteLogoMutation.isPending} onClick={() => deleteLogoMutation.mutate()} className="gap-1.5"
              >
                <Trash2 size={13} /> Delete
              </Button>
            )}
          </div>
          <span className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Max 5 MB. Appears on every invoice until replaced or deleted.</span>
        </div>
      </motion.div>

      <motion.form
        {...fadeUp(reducedMotion, 0.08)} onSubmit={(e) => e.preventDefault()}
        className="solid-surface rounded-[14px] p-6 space-y-4"
      >
        <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>Business details</p>
        <FormField label="Legal name" testId={SETTINGS.legalNameInput} value={contact.legal_name} onChange={(v) => setContact((c) => ({ ...c, legal_name: v }))} />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Phone" type="tel" testId={SETTINGS.phoneInput} value={contact.phone} onChange={(v) => setContact((c) => ({ ...c, phone: v }))} />
          <FormField label="Email" type="email" testId={SETTINGS.emailInput} value={contact.email} onChange={(v) => setContact((c) => ({ ...c, email: v }))} />
        </div>
        <FormField label="Address line 1" testId={SETTINGS.addressLine1Input} value={contact.line1} onChange={(v) => setContact((c) => ({ ...c, line1: v }))} />
        <FormField label="Address line 2" testId={SETTINGS.addressLine2Input} value={contact.line2} onChange={(v) => setContact((c) => ({ ...c, line2: v }))} />
        <div className="grid grid-cols-3 gap-3">
          <FormField label="City" testId={SETTINGS.cityInput} value={contact.city} onChange={(v) => setContact((c) => ({ ...c, city: v }))} />
          <FormField label="State" testId={SETTINGS.stateInput} value={contact.state} onChange={(v) => setContact((c) => ({ ...c, state: v }))} />
          <FormField label="Pincode" testId={SETTINGS.pincodeInput} value={contact.pincode} onChange={(v) => setContact((c) => ({ ...c, pincode: v }))} />
        </div>
        <SubmitButton
          testId={SETTINGS.saveContactButton} pending={saveContactMutation.isPending} success={saveSuccess}
          onClick={() => saveContactMutation.mutate()} label="Save business details" successLabel="Saved"
        />
      </motion.form>

      <motion.div {...fadeUp(reducedMotion, 0.1)} className="solid-surface rounded-[14px] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>GST</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Not every gym is GST-registered — turn this off to keep invoices GST-free.</p>
          </div>
          <Switch
            data-testid={SETTINGS.gstApplicableToggle} checked={gst.gst_applicable}
            onCheckedChange={(v) => setGst((g) => ({ ...g, gst_applicable: v }))}
          />
        </div>
        {gst.gst_applicable && (
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="GSTIN" testId={SETTINGS.gstinInput} value={gst.gstin} onChange={(v) => setGst((g) => ({ ...g, gstin: v }))} />
              <FormField label="Place of supply" testId={SETTINGS.placeOfSupplyInput} value={gst.place_of_supply} onChange={(v) => setGst((g) => ({ ...g, place_of_supply: v }))} />
            </div>
            <FormField
              label="GST rate %" type="number" testId={SETTINGS.gstRateInput} value={gst.gst_rate_percent}
              onChange={(v) => setGst((g) => ({ ...g, gst_rate_percent: v }))}
              helperText={`Split evenly as ${(Number(gst.gst_rate_percent) / 2 || 0).toFixed(1)}% CGST + ${(Number(gst.gst_rate_percent) / 2 || 0).toFixed(1)}% SGST`}
            />
          </div>
        )}
        <SubmitButton
          testId={SETTINGS.saveGstButton} pending={saveGstMutation.isPending} success={gstSuccess}
          onClick={() => saveGstMutation.mutate()} label="Save GST settings" successLabel="Saved"
        />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.16)} className="solid-surface rounded-[14px] p-6 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>Invoice terms &amp; conditions</p>
          {profileQ.data?.invoice_terms_is_default && (
            <span data-testid={SETTINGS.termsDefaultBadge} className="status-pill">Starter text — edit anytime</span>
          )}
        </div>
        <Label>Shown on every invoice, below the amount in words</Label>
        <Textarea
          data-testid={SETTINGS.termsTextarea} rows={10} value={terms}
          onChange={(e) => setTerms(e.target.value)} className="text-sm"
        />
        <SubmitButton
          testId={SETTINGS.saveTermsButton} pending={saveTermsMutation.isPending} success={termsSuccess}
          onClick={() => saveTermsMutation.mutate()} label="Save terms & conditions" successLabel="Saved"
        />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.2)} data-testid={SETTINGS.integrationsSection} className="solid-surface rounded-[14px] p-6 space-y-3">
        <div>
          <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>Integrations</p>
          <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Read-only — your Kairoo Implementation contact connects and tests these on your behalf.</p>
        </div>
        {(integrationsQ.data || []).length === 0 ? (
          <p className="text-sm py-2" style={{ color: "var(--scarlet-text-secondary)" }}>No connectors set up for your gym yet.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--scarlet-hairline)" }}>
            {(integrationsQ.data || []).map((c) => (
              <div key={c.id} data-testid={SETTINGS.integrationRow(c.id)} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2.5">
                  <MessageCircle size={16} style={{ color: "var(--scarlet-text-secondary)" }} />
                  <div>
                    <p className="text-sm font-medium capitalize" style={{ color: "var(--scarlet-text)" }}>{c.channel} · {c.provider}</p>
                    {c.public_config?.verified_name && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{c.public_config.verified_name} ({c.from_address})</p>}
                  </div>
                </div>
                <span className="text-sm font-semibold" style={{ color: STATUS_COLORS[c.status] || "var(--scarlet-text-secondary)" }}>{STATUS_LABELS[c.status] || c.status}</span>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
